"use client";

/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery } from "@tanstack/react-query";
import { req } from "../api";
import {
  PaginationResponse,
  PagingProps,
  type ApiPagedResponse,
  type PaginationProps,
} from "../types";

interface PaginatedQueryOptions<TData, TSelected = TData> {
  section: string;
  currentPage: number;
  pageSize?: number;
  queryKey?: string[];
  baseKey?: string[];
  queryFn?: (
    urlParams: string,
    section?: string,
    signal?: AbortSignal,
  ) => Promise<ApiPagedResponse<TData>>;
  select?: (item: TData) => TSelected;
  filters?: Record<string, string>;
  enabled?: boolean;
}

export interface PaginatedQueryResult<TSelected> {
  data: TSelected[];
  isLoading: boolean;
  error: Error | null;
  pagination: PaginationProps;
  refetch: () => void;
}

export function usePaginatedQuery<TData, TSelected = TData>({
  section,
  currentPage,
  pageSize = 6,
  queryKey: providedQueryKey,
  baseKey,
  queryFn,
  select,
  filters,
  enabled = true,
}: PaginatedQueryOptions<TData, TSelected>): PaginatedQueryResult<TSelected> {
  const activeFilters = !filters
    ? {}
    : Object.entries(filters)
        .sort(([a], [b]) => a.localeCompare(b))
        .filter(([_, value]) => value && value !== "" && value !== "all")
        .filter(([_, value]) => !baseKey?.includes(value))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const urlParams = new URLSearchParams({
    currPage: currentPage.toString(),
    perPage: pageSize.toString(),
    ...activeFilters,
  }).toString();

  const queryKey =
    providedQueryKey ||
    (baseKey ? [...baseKey, urlParams] : [section, urlParams]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    retry: 0,
    queryFn: ({ signal }) =>
      queryFn?.(urlParams, section, signal) ??
      req<ApiPagedResponse<TData>>(`${section}?${urlParams}`, "get", undefined, undefined, signal),
    select: (response: ApiPagedResponse<TData>) => {
      const items = response.items || [];
      const transformedData = select
        ? items.map(select)
        : (items as unknown as TSelected[]);

      return {
        data: transformedData,
        pagination: {
          currentPage: response.currPage,
          pageSize: response.perPage,
          totalPages: response.totalPages,
          totalItems: response.totalItems,
        },
      };
    },
    enabled,
  });

  return {
    data: data?.data ?? [],
    isLoading,
    error,
    pagination: data?.pagination ?? {
      currentPage,
      pageSize,
      totalPages: 0,
      totalItems: 0,
    },
    refetch,
  };
}
